import { releaseStepsToStepRunGraph } from './../helpers/demoHelpers';
import {
    AppPlatform,
    AppStoreData,
    AppleAppStoreData,
    Release,
    ReleaseStepStoreReviewData,
    ReleaseStepSubmissionData,
    ReleaseStepTakeoffData,
    ReleaseStepType,
    ReleaseStep,
} from '../types';
import { RecursiveStringifyEnums } from './buildDistro';
import { baseReleaseStepsForPlatform } from '@/testData/releaseSteps';
import { baseReleaseiOS } from '@/testData/baseReleaseiOS';
import { baseReleaseOTA } from '@/testData/baseReleaseOTA';
import { baseReleaseAndroid } from '@/testData/baseReleaseAndroid';
import { fillStepsWithReleaseData } from '@/testData/baseRelease';

export const injectAppStoreData = (releaseSteps: ReleaseStep[], appStoreData: AppStoreData): ReleaseStep[] => {
    releaseSteps = releaseSteps.map((step) => {
        if (step.type === ReleaseStepType.Submission) {
            const data = step.data as ReleaseStepSubmissionData;
            data.appStoreData = appStoreData;
        } else if (step.type === ReleaseStepType.StoreReview) {
            const data = step.data as ReleaseStepStoreReviewData;
            data.appStoreData = appStoreData;
        } else if (step.type === ReleaseStepType.Takeoff) {
            const data = step.data as ReleaseStepTakeoffData;
            data.appStoreData = appStoreData;
        }
        return step;
    });

    return releaseSteps;
};

export const mockReviewSubmissionAppStoreData = (appStoreData?: Partial<AppleAppStoreData>) => {
    const defaultAppStoreData: RecursiveStringifyEnums<AppleAppStoreData> = {
        customProductPages: [
            {
                ascLink:
                    'https://appstoreconnect.apple.com/apps/1234/distribution/productpages/92904ee5-8644-4d83-b585-e56dfab21062',
                firstVersionLocalization: {
                    appPreviewSets: [],
                    id: 'e389095f-a40a-4ee3-873b-194456e096d9',
                    locale: 'en-US',
                    promotionalText: '',
                    screenshotSets: [
                        {
                            id: '57cd0dd4-70e4-45c6-b722-95971b96688e',
                            screenshotDisplayType: 'APP_IPHONE_55',
                            screenshots: [
                                {
                                    height: 2208,
                                    id: '',
                                    uploadError: null,
                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                    width: 1242,
                                },
                                {
                                    height: 2208,
                                    id: '',
                                    uploadError: null,
                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                    width: 1242,
                                },
                            ],
                        },
                        {
                            id: '2d0b079f-06c5-4df0-8ea8-d5b0269dd0db',
                            screenshotDisplayType: 'APP_IPHONE_65',
                            screenshots: [
                                {
                                    height: 2208,
                                    id: '',
                                    uploadError: null,
                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                    width: 1242,
                                },
                                {
                                    height: 2208,
                                    id: '',
                                    uploadError: null,
                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                    width: 1242,
                                },
                            ],
                        },
                    ],
                },
                id: '92904ee5-8644-4d83-b585-e56dfab21068',
                name: 'Another product page',
                url: 'https://apps.apple.com/us/app/appollo/id595068603?ppid=92904ee5-8644-4d83-b585-e56dfab21068',
                versions: [
                    {
                        id: '21dd3774-cee0-4b76-97bf-6736f9ee1ed1',
                        state: 'APPROVED',
                        version: '1',
                    },
                ],
                visible: true,
            },
        ],
        inAppEvents: [
            {
                ascLink: 'https://appstoreconnect.apple.com/apps/595068603/distribution/events/6502416798',
                badge: 'LIVE_EVENT',
                eventState: 'DRAFT',
                id: '6502416798',
                longDescription: 'Join us for a live stream demo...',
                previewImages: [
                    {
                        height: 1080,
                        type: 'EVENT_CARD',
                        url: 'https://is1-ssl.mzstatic.com/image/thumb/eVpKWMMqHdOq-dYA8MYZOw/192x108bb.jpg',
                        width: 1920,
                    },
                    {
                        height: 1920,
                        type: 'EVENT_DETAILS_PAGE',
                        url: 'https://is1-ssl.mzstatic.com/image/thumb/t-pvtnJkPMCLjjkRyB5BGA/108x192bb.jpg',
                        width: 1080,
                    },
                ],
                primaryLocale: 'en-US',
                referenceName: 'New Live Stream Event',
                shortDescription: 'Live stream demo of New',
                territorySchedules: [
                    {
                        eventEnd: '2024-05-13T20:00:00Z',
                        eventStart: '2024-05-13T18:00:00Z',
                        publishStart: '2024-05-09T22:00:00Z',
                        territories: [],
                    },
                ],
            },
        ],
        relevantReviewSubmission: {
            appStoreVersionForReviewId: '80aa1499-4afa-46a1-84b8-7377f74cfb66',
            id: 'b8ee3ad0-9e2f-44c8-8201-af246d8881a5',
            isAppBuildSubmissionBlocked: false,
            reviewSubmissionASCLink:
                'https://appstoreconnect.apple.com/apps/595068603/distribution/reviewsubmissions/details/b8ee3ad0-9e2f-44c8-8201-af246d8881a5',
            reviewSubmissionItems: [
                {
                    id: 'YjhlZTNhZDAtOWUyZi00NGM4LTgyMDEtYWYyNDZkODg4MWEyfDZ8ODY1NjczMDQ1',
                    item: null,
                    itemId: '80aa1499-4afa-46a1-84b8-7377f74cfb66',
                    state: 'READY_FOR_REVIEW',
                    type: 'appStoreVersions',
                },
                {
                    id: 'YjhlZTNhZDAtOWUyZi00NGM4LTgyMDEtYWYyNDZkODg4MWEyfDV8YjFiYjY4MTctZjAwYi00MTQxLTlhMDUtNTdhMzk5ZjQ3YTU0',
                    item: {
                        ascLink:
                            'https://appstoreconnect.apple.com/apps/595068603/distribution/productpages/321b8676-a7a2-4a2e-8614-6ea4e7a9a2f3',
                        firstVersionLocalization: {
                            appPreviewSets: [],
                            id: '8a4cd3d8-d561-4e28-8d7d-bc17ad7f2640',
                            locale: 'en-US',
                            promotionalText: '',
                            screenshotSets: [
                                {
                                    id: '57cd0dd4-70e4-45c6-b722-95971b96688e',
                                    screenshotDisplayType: 'APP_IPHONE_55',
                                    screenshots: [
                                        {
                                            height: 2208,
                                            id: '',
                                            uploadError: null,
                                            url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                            width: 1242,
                                        },
                                        {
                                            height: 2208,
                                            id: '',
                                            uploadError: null,
                                            url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                            width: 1242,
                                        },
                                    ],
                                },
                                {
                                    id: '2d0b079f-06c5-4df0-8ea8-d5b0269dd0db',
                                    screenshotDisplayType: 'APP_IPHONE_65',
                                    screenshots: [
                                        {
                                            height: 2208,
                                            id: '',
                                            uploadError: null,
                                            url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                            width: 1242,
                                        },
                                        {
                                            height: 2208,
                                            id: '',
                                            uploadError: null,
                                            url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                            width: 1242,
                                        },
                                    ],
                                },
                            ],
                        },
                        id: '321b8676-a7a2-4a2e-8614-6ea4e7a9a2f3',
                        name: 'New Product Page',
                        url: 'https://apps.apple.com/us/app/appollo/id595068603?ppid=321b8676-a7a2-4a2e-8614-6ea4e7a9a2f3',
                        versions: [
                            {
                                id: 'b1bb6817-f00b-4141-9a05-57a399f47a54',
                                state: 'READY_FOR_REVIEW',
                                version: '1',
                            },
                        ],
                        visible: true,
                    },
                    itemId: 'b1bb6817-f00b-4141-9a05-57a399f47a54',
                    state: 'READY_FOR_REVIEW',
                    type: 'appCustomProductPageVersions',
                },
            ],
            state: 'READY_FOR_REVIEW',
        },
        reviewDetail: {
            contactEmail: 'aearhart@demo.com',
            contactFirstName: 'Amelia',
            contactLastName: 'Earhart',
            contactPhone: '+(123)456-7891',
            demoAccountName: null,
            demoAccountPassword: null,
            demoAccountRequired: false,
            notes: '',
            id: '1234',
            reviewAttachments: [],
        },
        versionReviewSubmissionItem: {
            earliestReleaseDate: null,
            id: '80aa1499-4afa-46a1-84b8-7377f74cfb66',
            releaseType: 'MANUAL',
            state: 'READY_FOR_REVIEW',
        },
        phasedRelease: {
            dayNumber: 0,
            isManuallyAcceleratedPhasedRollout: false,
            isRunwayAcceleratedRollout: false,
            isRunwayPaused: false,
            startDate: null,
            state: 'INACTIVE',
            userFraction: 0,
            blackoutEndsAt: '2024-05-10T13:33:32.988339868-07:00',
            changedByAutomation: null,
            completedAt: null,
            isOnBlackoutPeriod: false,
            phasedReleasePercent: 0,
            isSuperseded: false,
        },
    };
    return {
        ...defaultAppStoreData,
        ...appStoreData,
    } as AppleAppStoreData;
};

export const mockReleaseForPlatform: (platform: AppPlatform) => RecursiveStringifyEnums<Release> = (platform) => {
    const release =
        platform === AppPlatform.Ios ? baseReleaseiOS()
        : platform === AppPlatform.Android ? baseReleaseAndroid()
        : platform === AppPlatform.ReactNativeOTA ? baseReleaseOTA()
        : null;

    let steps = baseReleaseStepsForPlatform(platform);
    steps = fillStepsWithReleaseData(steps as ReleaseStep[], release, platform);
    release.stepRunGraph = releaseStepsToStepRunGraph(steps);

    return release as RecursiveStringifyEnums<Release>;
};
